import { CATEGORIES } from "./categories";
import type { QuizQuestion } from "./types";

// McKayla's September 2026 quiz rewrite — 15 personality + 4 profile = 19 total.
// Prior version (July 2026, commit 17440fe): 20 personality + 4 profile = 24.
// Prior-prior version (pre-2026-07-20): 13 personality + 4 profile = 17.
//
// Scoring guide (McKayla, Quiz - Sept 2026.pdf p6-7): every personality question
// maps one option per core. She wrote them A=DC B=WA C=SS D=FF E=GA and asked us
// to randomize the display order — done below; each option keeps its `category`.
//
// Questions 2, 5, 8, 11 are "Money Story" questions (background/reflective) that
// follow the same category mapping as the scored questions. They are included as
// personality-type questions so they contribute to the score.
export const QUESTIONS: QuizQuestion[] = [
  // Q1
  {
    question:
      "An unexpected $5,000 hits your account. What's your first thought?",
    options: [
      {
        text: "I'd feel better knowing it's there if I need it.",
        category: CATEGORIES.SAFETY_SEEKER,
      },
      {
        text: "There's something I've been wanting to make happen.",
        category: CATEGORIES.DREAM_CHASER,
      },
      {
        text: "This could pay for something I've been wanting to experience.",
        category: CATEGORIES.FREEDOM_FINDER,
      },
      {
        text: "What's the smartest thing I could do with it?",
        category: CATEGORIES.WEALTH_ARCHITECT,
      },
      {
        text: "Is there something meaningful I could do with it?",
        category: CATEGORIES.GENEROUS_ALTRUIST,
      },
    ],
    type: "personality",
  },
  // Q2 — Money Story
  {
    question:
      "Growing up, how would you describe the way money was talked about in your home?",
    options: [
      {
        text: "Money was something we were careful about and didn't take for granted.",
        category: CATEGORIES.WEALTH_ARCHITECT,
      },
      {
        text: "Money was often connected to helping, providing for, or taking care of others.",
        category: CATEGORIES.GENEROUS_ALTRUIST,
      },
      {
        text: "We talked pretty openly about money and financial decisions.",
        category: CATEGORIES.DREAM_CHASER,
      },
      {
        text: "Money wasn't discussed very much, at least not with me.",
        category: CATEGORIES.SAFETY_SEEKER,
      },
      {
        text: "Money could be a source of stress or tension.",
        category: CATEGORIES.FREEDOM_FINDER,
      },
    ],
    type: "personality",
  },
  // Q3
  {
    question:
      "Your investments have had a strong year, and your account is up more than you expected. What's your first instinct?",
    options: [
      {
        text: "Feel more comfortable using some of my money to enjoy life now.",
        category: CATEGORIES.FREEDOM_FINDER,
      },
      {
        text: "Think about what this progress could make possible for my bigger goals.",
        category: CATEGORIES.DREAM_CHASER,
      },
      {
        text: "Think about whether I should protect some of the gains I've made.",
        category: CATEGORIES.SAFETY_SEEKER,
      },
      {
        text: "Think about whether this puts me in a position to do more for the people or causes I care about.",
        category: CATEGORIES.GENEROUS_ALTRUIST,
      },
      {
        text: "Look at what's working and consider whether there are opportunities to build on it.",
        category: CATEGORIES.WEALTH_ARCHITECT,
      },
    ],
    type: "personality",
  },
  // Q4
  {
    question:
      "A great opportunity comes up, but you don't have all the information you'd like. What are you most likely to do?",
    options: [
      {
        text: "Make sure I understand the downside before moving forward.",
        category: CATEGORIES.SAFETY_SEEKER,
      },
      {
        text: "Consider how the decision fits with everything and everyone important to me.",
        category: CATEGORIES.GENEROUS_ALTRUIST,
      },
      {
        text: "Think about where it could lead and decide whether it's worth pursuing.",
        category: CATEGORIES.DREAM_CHASER,
      },
      {
        text: "Make the best decision I can and trust myself to adjust if needed.",
        category: CATEGORIES.FREEDOM_FINDER,
      },
      {
        text: "Learn as much as I reasonably can before deciding.",
        category: CATEGORIES.WEALTH_ARCHITECT,
      },
    ],
    type: "personality",
  },
  // Q5 — Money Story
  {
    question:
      "Think back to the first time you had money that felt like your own. What do you remember wanting to do with it?",
    options: [
      {
        text: "Use it for something fun, especially if I could enjoy it with someone else.",
        category: CATEGORIES.FREEDOM_FINDER,
      },
      {
        text: "Save it. I liked knowing I had money of my own.",
        category: CATEGORIES.SAFETY_SEEKER,
      },
      {
        text: "Buy something I'd been wanting for a while.",
        category: CATEGORIES.DREAM_CHASER,
      },
      {
        text: "I don't remember having a strong instinct either way.",
        category: CATEGORIES.GENEROUS_ALTRUIST,
      },
      {
        text: "Spend some and keep some for later.",
        category: CATEGORIES.WEALTH_ARCHITECT,
      },
    ],
    type: "personality",
  },
  // Q6
  {
    question:
      "When you feel like you're in a really good place financially, what usually gives you that feeling?",
    options: [
      {
        text: "I can see that the decisions I've made are paying off.",
        category: CATEGORIES.WEALTH_ARCHITECT,
      },
      {
        text: "I know I'm prepared if something unexpected happens.",
        category: CATEGORIES.SAFETY_SEEKER,
      },
      {
        text: "I have enough to take care of what's important to me and still give when I want to.",
        category: CATEGORIES.GENEROUS_ALTRUIST,
      },
      {
        text: "I know I'm making progress toward something important to me.",
        category: CATEGORIES.DREAM_CHASER,
      },
      {
        text: "I don't feel like money is limiting the choices I can make.",
        category: CATEGORIES.FREEDOM_FINDER,
      },
    ],
    type: "personality",
  },
  // Q7
  {
    question:
      "Your financial plan for the year gets completely thrown off. What's your natural reaction?",
    options: [
      {
        text: "Adjust. Plans change, and I don't need everything to go exactly as expected.",
        category: CATEGORIES.FREEDOM_FINDER,
      },
      {
        text: "Rework the plan without losing sight of what I'm working toward.",
        category: CATEGORIES.DREAM_CHASER,
      },
      {
        text: "Reevaluate my priorities and make sure the things that matter most are covered.",
        category: CATEGORIES.GENEROUS_ALTRUIST,
      },
      {
        text: "Focus first on making sure I'm still in a secure position.",
        category: CATEGORIES.SAFETY_SEEKER,
      },
      {
        text: "Figure out what changed and determine the best next move.",
        category: CATEGORIES.WEALTH_ARCHITECT,
      },
    ],
    type: "personality",
  },
  // Q8 — Money Story
  {
    question:
      "Growing up, which message about money do you remember most, whether it was said directly or simply understood?",
    options: [
      {
        text: "If you're fortunate enough to have more, you should help others.",
        category: CATEGORIES.GENEROUS_ALTRUIST,
      },
      {
        text: "Work hard and build something for yourself.",
        category: CATEGORIES.DREAM_CHASER,
      },
      {
        text: "Money gives you opportunities and choices.",
        category: CATEGORIES.SAFETY_SEEKER,
      },
      {
        text: "Be careful with money because you never know when you'll need it.",
        category: CATEGORIES.WEALTH_ARCHITECT,
      },
      {
        text: "Enjoy what you have. You can't take it with you.",
        category: CATEGORIES.FREEDOM_FINDER,
      },
    ],
    type: "personality",
  },
  // Q9
  {
    question:
      "You're given two equally good options. Which one are you more likely to choose?",
    options: [
      {
        text: "The one that gives me the most confidence in the outcome.",
        category: CATEGORIES.SAFETY_SEEKER,
      },
      {
        text: "The one with the most exciting future potential.",
        category: CATEGORIES.DREAM_CHASER,
      },
      {
        text: "The one that best supports what matters most in my life.",
        category: CATEGORIES.GENEROUS_ALTRUIST,
      },
      {
        text: "The one that appears to offer the strongest long-term value.",
        category: CATEGORIES.WEALTH_ARCHITECT,
      },
      {
        text: "The one that gives me the most flexibility down the road.",
        category: CATEGORIES.FREEDOM_FINDER,
      },
    ],
    type: "personality",
  },
  // Q10
  {
    question:
      "You look at your account and realize you've spent more than usual this month. What's your reaction?",
    options: [
      {
        text: "I want to see exactly where the extra spending came from.",
        category: CATEGORIES.WEALTH_ARCHITECT,
      },
      {
        text: "I think about whether that spending took away from another priority.",
        category: CATEGORIES.GENEROUS_ALTRUIST,
      },
      {
        text: "I naturally want to tighten things up the following month.",
        category: CATEGORIES.SAFETY_SEEKER,
      },
      {
        text: "I think about whether what I spent was actually worth it.",
        category: CATEGORIES.DREAM_CHASER,
      },
      {
        text: "If I can afford it and enjoyed it, I'm not going to beat myself up over it.",
        category: CATEGORIES.FREEDOM_FINDER,
      },
    ],
    type: "personality",
  },
  // Q11 — Money Story
  {
    question:
      "Looking back, which of these experiences do you think has influenced the way you think about money today?",
    options: [
      {
        text: "Seeing money create opportunities that otherwise wouldn't have been possible.",
        category: CATEGORIES.SAFETY_SEEKER,
      },
      {
        text: "Watching someone close to me work hard to build financial success.",
        category: CATEGORIES.DREAM_CHASER,
      },
      {
        text: "Seeing money used to support family, friends, or others in need.",
        category: CATEGORIES.GENEROUS_ALTRUIST,
      },
      {
        text: "Experiencing or witnessing financial uncertainty.",
        category: CATEGORIES.WEALTH_ARCHITECT,
      },
      {
        text: "Seeing someone sacrifice too much of their life in pursuit of financial success.",
        category: CATEGORIES.FREEDOM_FINDER,
      },
    ],
    type: "personality",
  },
  // Q12
  {
    question:
      "Something doesn't go according to plan financially. What helps you regain confidence?",
    options: [
      {
        text: "Knowing I still have choices and can adjust along the way.",
        category: CATEGORIES.FREEDOM_FINDER,
      },
      {
        text: "Understanding what happened and what I can do differently.",
        category: CATEGORIES.WEALTH_ARCHITECT,
      },
      {
        text: "Reminding myself where I'm headed and why.",
        category: CATEGORIES.DREAM_CHASER,
      },
      {
        text: "Creating a clear plan for what comes next.",
        category: CATEGORIES.SAFETY_SEEKER,
      },
      {
        text: "Refocusing on what's most important to me.",
        category: CATEGORIES.GENEROUS_ALTRUIST,
      },
    ],
    type: "personality",
  },
  // Q13
  {
    question:
      "Which financial milestone would feel the most rewarding?",
    options: [
      {
        text: "Knowing I could handle a major unexpected expense without worry.",
        category: CATEGORIES.SAFETY_SEEKER,
      },
      {
        text: "Reaching a level of wealth I once thought was out of reach.",
        category: CATEGORIES.WEALTH_ARCHITECT,
      },
      {
        text: "Having enough flexibility to choose how I spend more of my time.",
        category: CATEGORIES.FREEDOM_FINDER,
      },
      {
        text: "Having the resources to pursue something I've always wanted to do.",
        category: CATEGORIES.DREAM_CHASER,
      },
      {
        text: "Being able to provide meaningful support to people or causes I care about.",
        category: CATEGORIES.GENEROUS_ALTRUIST,
      },
    ],
    type: "personality",
  },
  // Q14
  {
    question:
      "Which comment would challenge you the most?",
    options: [
      {
        text: "\"Not every decision has to be optimized.\"",
        category: CATEGORIES.WEALTH_ARCHITECT,
      },
      {
        text: "\"At some point, you have to stop planning and go for it.\"",
        category: CATEGORIES.DREAM_CHASER,
      },
      {
        text: "\"It's okay for your own needs to come first.\"",
        category: CATEGORIES.GENEROUS_ALTRUIST,
      },
      {
        text: "\"Sometimes committing to something creates more opportunity, not less.\"",
        category: CATEGORIES.FREEDOM_FINDER,
      },
      {
        text: "\"You can't prepare for every possibility.\"",
        category: CATEGORIES.SAFETY_SEEKER,
      },
    ],
    type: "personality",
  },
  // Q15
  {
    question:
      "Finish this sentence: I want my money to give me more...",
    options: [
      {
        text: "Confidence that I'm prepared for the future.",
        category: CATEGORIES.SAFETY_SEEKER,
      },
      {
        text: "Possibility to create the future I imagine.",
        category: CATEGORIES.DREAM_CHASER,
      },
      {
        text: "Ability to make a difference in the lives of others.",
        category: CATEGORIES.GENEROUS_ALTRUIST,
      },
      {
        text: "Opportunity to keep building and growing.",
        category: CATEGORIES.WEALTH_ARCHITECT,
      },
      {
        text: "Freedom to live life on my own terms.",
        category: CATEGORIES.FREEDOM_FINDER,
      },
    ],
    type: "personality",
  },
  // --- Profile questions (unchanged from prior version) ---
  {
    question: "What is your approximate net worth?",
    options: [
      {
        text: "Less than $250k",
      },
      {
        text: "$250k to $500k",
      },
      {
        text: "$500k to $1mm",
      },
      {
        text: "$1mm to $5mm",
      },
      {
        text: "$5mm to $10mm",
      },
      {
        text: "$10mm +",
      },
    ],
    type: "profile",
  },
  {
    question: "What is your age group?",
    options: [
      {
        text: "Under 30 years of age",
      },
      {
        text: "30 to 45",
      },
      {
        text: "45-60",
      },
      {
        text: "60-80",
      },
      {
        text: "80+",
      },
    ],
    type: "profile",
  },
  {
    question: "What stage of life are you in?",
    options: [
      {
        text: "I am about to graduate high school or college and enter the work force.",
      },
      {
        text: "I am raising a family and working to support them.",
      },
      {
        text: "I am getting ready to retire and start living the life I've dreamed of.",
      },
      {
        text: "I am a business owner growing and accumulating.",
      },
      {
        text: "I am business owner looking to sell my business and retire.",
      },
    ],
    type: "profile",
  },
  {
    question:
      "When it comes to money and finances, I find most of my information from:",
    options: [
      {
        text: "Financial Professionals such as a Financial Advisor or Tax Professional.",
      },
      {
        text: "AI Tools such as Chat GPT, Gemini, etc.",
      },
      {
        text: "Instagram, Tik Tok, and other social media channels.",
      },
      {
        text: "I research most financial information myself from various internet sites.",
      },
      {
        text: "I read financial newsletters from trusted sources such as Barron's, Forbes, and the Wall Street Journal.",
      },
    ],
    type: "profile",
  },
  // Income (McKayla 2026-10-05, Gmail thread 1a08d0461d6b0790): optional, never
  // scored (profile answers are saved to quizzes.info, not the personality score).
  // Appended LAST on purpose: stored answers resolve positionally against
  // QUESTIONS (legacyAnswers.ts), so inserting mid-list would shift older rows.
  // The question text is also the key the admin Income column reads.
  {
    question: "What is your approximate annual household income?",
    helper: "Optional — this does not impact your Money Personality results.",
    options: [
      { text: "Under $50,000" },
      { text: "$50,000–$99,999" },
      { text: "$100,000–$149,999" },
      { text: "$150,000–$249,999" },
      { text: "$250,000–$499,999" },
      { text: "$500,000–$999,999" },
      { text: "$1,000,000+" },
      { text: "Prefer not to answer" },
    ],
    type: "profile",
  },
];

// The sub-personality quiz (SUB_CATEGORY_MAPPINGS + SPECIFIC_QUESTIONS — five
// extra questions asked after the main quiz, scoring one of ten sub-types) was
// REMOVED here on 2026-08-20 (ClickUp 86bb0f9vz).
//
// McKayla on the 2026-08-20 call: "They don't need to know if they're a
// subtype... So we're gonna keep the five personalities instead of the 10."
// Client-confirmed twice; see DECISIONS.md.
//
// Do not reinstate this to "add depth" to the quiz — cutting its length is an
// explicit client goal (she measured 15 minutes and quit halfway). Recover from
// git history if the five cores are ever subdivided again.
